/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { getAniosCarga, getAnioCargaMin, getAnioCargaMax } from "../../constants/anios";
import { getLocale } from "../../utils/getLocale";
import { useCurrency } from "../../utils/useCurrency";
import styles from "./FlashEditModal.module.scss";

const MONTHS = [
  { value: "01", label: "ENE" },
  { value: "02", label: "FEB" },
  { value: "03", label: "MAR" },
  { value: "04", label: "ABR" },
  { value: "05", label: "MAY" },
  { value: "06", label: "JUN" },
  { value: "07", label: "JUL" },
  { value: "08", label: "AGO" },
  { value: "09", label: "SEP" },
  { value: "10", label: "OCT" },
  { value: "11", label: "NOV" },
  { value: "12", label: "DIC" },
];

const CURRENT_YEAR = new Date().getFullYear();

// APX (Publicis) — intermediario "PUBLICIS | APX" (capa aditiva, NO override; ver 9.32). El selector
// de acuerdos lo ofrece como caso especial cuando el holding del cliente coincide con el de APX, y
// al elegirlo escribe `apx_id` en vez de `intermediario_override_id`. Comparación por ID (10.7).
const APX_INTERMEDIARIO_ID = 126;

const MIN_YEAR = getAnioCargaMin();
const MAX_YEAR = getAnioCargaMax();

// Convierte "MM-YYYY" a un entero comparable (año*12 + mes) para ordenar/validar meses.
const monthVal = (mesStr) => {
  const [mm, yyyy] = (mesStr || "").split("-");
  return Number(yyyy || 0) * 12 + Number(mm || 0);
};

const MonthYearPicker = ({ month, year, onChange, label, disabled, hideLabel, compact }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayYear, setDisplayYear] = useState(Number(year) || CURRENT_YEAR);
  const ref = useRef(null);

  useEffect(() => {
    setDisplayYear(Number(year) || CURRENT_YEAR);
  }, [year]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const monthLabel = MONTHS.find((m) => m.value === month)?.label || "---";

  return (
    <div className={styles.pickerWrapper} ref={ref}>
      {!hideLabel && <label className={styles.fieldLabel}>{label}</label>}
      <button
        type="button"
        className={styles.pickerToggle}
        style={{
          ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
          ...(compact ? { height: "auto", padding: "2px 8px", fontSize: "13px" } : {}),
        }}
        onClick={() => {
          if (disabled) return;
          setIsOpen(!isOpen);
        }}
      >
        {monthLabel} {year}
      </button>
      {isOpen && (
        <div className={styles.pickerDropdown}>
          <div className={styles.yearNav}>
            <button
              type="button"
              className={styles.yearArrow}
              onClick={() => setDisplayYear((y) => Math.max(MIN_YEAR, y - 1))}
              disabled={displayYear <= MIN_YEAR}
            >
              &lsaquo;
            </button>
            <span className={styles.yearLabel}>{displayYear}</span>
            <button
              type="button"
              className={styles.yearArrow}
              onClick={() => setDisplayYear((y) => Math.min(MAX_YEAR, y + 1))}
              disabled={displayYear >= MAX_YEAR}
            >
              &rsaquo;
            </button>
          </div>
          <div className={styles.monthGrid}>
            {MONTHS.map((m) => {
              const isSelected = month === m.value && String(displayYear) === String(year);
              return (
                <button
                  key={m.value}
                  type="button"
                  className={`${styles.monthCell} ${isSelected ? styles.monthSelected : ""}`}
                  onClick={() => {
                    onChange(m.value, String(displayYear));
                    setIsOpen(false);
                  }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Modal reutilizable para edición rápida (Flash) de oportunidades.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - opportunity: objeto oportunidad ({ id, estatus, mes_cierre, mes_factura })
 *  - onSave: (updatedFields) => Promise<void>
 *    Recibe { estatus, mes_cierre, mes_factura, ano } listos para Supabase.
 *  - saving: boolean (estado de guardado externo)
 *  - hideFacturacion: boolean (oculta picker facturación, ej. contratos)
 *  - subtitle: string (texto extra en el header, ej. nombre empresa)
 */
export const FlashEditModal = ({
  isOpen,
  onClose,
  opportunity,
  onSave,
  saving,
  hideFacturacion,
  subtitle,
  intermediarios = [],
  clientes = [],
}) => {
  const [estatus, setEstatus] = useState("");
  const [mesCierre, setMesCierre] = useState("01");
  const [anoCierre, setAnoCierre] = useState(String(CURRENT_YEAR));
  const [mesFactura, setMesFactura] = useState("01");
  const [anoFactura, setAnoFactura] = useState(String(CURRENT_YEAR));
  const [cuotas, setCuotas] = useState([]);
  const [focusedImporteIdx, setFocusedImporteIdx] = useState(null);
  const [acuerdoId, setAcuerdoId] = useState("");
  const { formatAmount } = useCurrency();

  const moneda = opportunity?.moneda_dolar === true ? "USD" : opportunity?.pais === "Perú" ? "PEN" : "CLP";
  const locale = getLocale(opportunity?.pais);
  const formatMiles = (n) => (n === "" || n == null || isNaN(n) ? "" : Number(n).toLocaleString(locale));

  useEffect(() => {
    if (opportunity) {
      setEstatus(opportunity.estatus || "");
      if (opportunity.mes_cierre) {
        const [mc, ac] = opportunity.mes_cierre.split("-");
        setMesCierre(mc || "01");
        setAnoCierre(ac || String(CURRENT_YEAR));
      }
      if (opportunity.mes_factura) {
        const [mf, af] = opportunity.mes_factura.split("-");
        setMesFactura(mf || "01");
        setAnoFactura(af || String(CURRENT_YEAR));
      }
      if (opportunity.meses && Array.isArray(opportunity.meses)) {
        setCuotas([...opportunity.meses]);
      }
      // Preselección del acuerdo: APX (apx_id) tiene prioridad; si no, el override; si ninguno, "".
      // Son mutuamente excluyentes (una venta no lleva override Y apx).
      setAcuerdoId(
        opportunity.apx_id != null
          ? String(opportunity.apx_id)
          : opportunity.intermediario_override_id != null
            ? String(opportunity.intermediario_override_id)
            : ""
      );
    }
  }, [opportunity]);

  const handleSave = useCallback(async () => {
    if (!estatus) {
      toast.error("Selecciona un estatus.");
      return;
    }
    if (hideFacturacion) {
      // Guardrail: una cuota no facturada no puede quedar antes del mes de la
      // última cuota ya facturada (no se factura "hacia atrás").
      const facturadas = cuotas.filter((c) => c.facturanumero);
      if (facturadas.length > 0) {
        const ultimaFacturada = facturadas.reduce((a, b) => (monthVal(b.mes) > monthVal(a.mes) ? b : a));
        const minVal = monthVal(ultimaFacturada.mes);
        const idxInvalida = cuotas.findIndex((c) => !c.facturanumero && monthVal(c.mes) < minVal);
        if (idxInvalida !== -1) {
          const [mmMin, yyyyMin] = (ultimaFacturada.mes || "").split("-");
          const labelMin = `${MONTHS.find((m) => m.value === mmMin)?.label || mmMin}/${yyyyMin}`;
          toast.error(
            `La cuota ${idxInvalida + 1} no puede tener fecha anterior a ${labelMin} (última cuota facturada)`
          );
          return;
        }
      }
      const suma = cuotas.reduce((acc, c) => acc + Number(c.importe || 0), 0);
      const total = Number(opportunity?.importe_total || opportunity?.importe || 0);
      if (Math.round(suma) !== Math.round(total)) {
        toast.error(
          `La suma de cuotas (${formatAmount(suma, moneda)}) no coincide con el total del contrato (${formatAmount(total, moneda)})`
        );
        return;
      }
    } else {
      const cierreDate = new Date(`${anoCierre}-${mesCierre}-01`);
      const facturaDate = new Date(`${anoFactura}-${mesFactura}-01`);
      if (cierreDate > facturaDate) {
        toast.error("El cierre debe ser igual o anterior a la facturación.");
        return;
      }
    }
    // Ruteo del acuerdo elegido: id 126 (APX) → escribe apx_id (capa aditiva); cualquier otro id
    // (acuerdo de volumen) → escribe intermediario_override_id (swap). Mutuamente excluyentes:
    // el que no aplica se setea null. "Sin acuerdo" → ambos null.
    const idAcuerdo = acuerdoId ? Number(acuerdoId) : null;
    const esApx = idAcuerdo === APX_INTERMEDIARIO_ID;
    const updatedFields = {
      estatus,
      mes_cierre: `${mesCierre.padStart(2, "0")}-${anoCierre}`,
      intermediario_override_id: esApx ? null : idAcuerdo,
      apx_id: esApx ? APX_INTERMEDIARIO_ID : null,
      updated_at: new Date().toISOString(),
    };
    if (hideFacturacion) {
      updatedFields.meses = cuotas;
    } else {
      updatedFields.mes_factura = `${mesFactura.padStart(2, "0")}-${anoFactura}`;
      updatedFields.ano = parseInt(anoFactura, 10);
    }
    await onSave(updatedFields);
  }, [
    estatus,
    mesCierre,
    anoCierre,
    mesFactura,
    anoFactura,
    hideFacturacion,
    cuotas,
    acuerdoId,
    opportunity,
    onSave,
    formatAmount,
    moneda,
  ]);

  if (!opportunity) return null;

  const displayName = opportunity.clienteNombre || subtitle || `#${opportunity.id}`;
  const campaignLabel = opportunity.campana || opportunity.code || "";
  // Resolver el holding del cliente de la oportunidad (comparación por ID, regla 10.7).
  // Si la agencia del cliente tiene holding_id → ese es el holding; si no, la agencia ES el holding.
  const clienteOpp = clientes.find((c) => c.id === opportunity.client_id);
  const agenciaId = clienteOpp?.agencia != null ? Number(clienteOpp.agencia) : null;
  const agenciaInter = agenciaId != null ? intermediarios.find((i) => i.id === agenciaId) : null;
  const holdingResuelto = agenciaInter ? agenciaInter.holding_id ?? agenciaInter.id : null;
  const intermediariosOverride =
    holdingResuelto == null
      ? []
      : [...intermediarios]
          .filter((i) => i.es_acuerdo_volumen && (i.holding_id === holdingResuelto || i.id === holdingResuelto))
          .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"));
  // APX (Publicis): caso especial — se ofrece si el holding del cliente coincide con el holding de
  // APX (derivado de la fila 126, sin hardcodear el 117). NO usa es_acuerdo_volumen (no es override).
  const apxInter = intermediarios.find((i) => i.id === APX_INTERMEDIARIO_ID);
  const ofreceApx = !!apxInter && holdingResuelto != null && apxInter.holding_id === holdingResuelto;
  // Lista unificada para el <select>: cada opción lleva id, label y tipo ('override' | 'apx').
  const opcionesAcuerdo = [
    ...intermediariosOverride.map((i) => ({ id: i.id, label: i.nombre, tipo: "override" })),
    ...(ofreceApx ? [{ id: APX_INTERMEDIARIO_ID, label: "APX (Publicis)", tipo: "apx" }] : []),
  ];
  const sinAcuerdos = opcionesAcuerdo.length === 0;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered className={styles.flashModal}>
      <ModalHeader toggle={onClose} className={styles.modalHeader}>
        <div className={styles.headerContent}>
          <span className={styles.headerTitle}>{displayName}</span>
          {campaignLabel && <span className={styles.headerSubtitle}>{campaignLabel}</span>}
          <span className={styles.headerTotal}>
            {formatAmount(Number(opportunity?.importe_total || opportunity?.importe || 0), moneda)}
          </span>
        </div>
      </ModalHeader>
      <ModalBody className={styles.modalBody}>
        <div className={styles.formRow}>
          <div className={styles.formCol}>
            <label className={styles.fieldLabel}>Status</label>
            <select className={`form-control ${styles.fieldInput}`} value={estatus} onChange={(e) => setEstatus(e.target.value)}>
              <option value="">Seleccionar</option>
              <option value="100%">100%</option>
              <option value="90%">90%</option>
              <option value="75%">75%</option>
              <option value="50%">50%</option>
              <option value="0%">0%</option>
            </select>
          </div>
          <MonthYearPicker
            label="Cierre"
            month={mesCierre}
            year={anoCierre}
            disabled={estatus === "100%"}
            onChange={(m, y) => {
              setMesCierre(m);
              setAnoCierre(y);
            }}
          />
          {!hideFacturacion && (
            <MonthYearPicker
              label="Facturación"
              month={mesFactura}
              year={anoFactura}
              onChange={(m, y) => {
                setMesFactura(m);
                setAnoFactura(y);
              }}
            />
          )}
        </div>
        <div className={styles.formRow}>
          <div className={styles.formCol} style={{ flex: 1 }}>
            <label className={styles.fieldLabel}>Acuerdo</label>
            <select
              className={`form-control ${styles.fieldInput}`}
              value={sinAcuerdos ? "" : acuerdoId}
              onChange={(e) => setAcuerdoId(e.target.value)}
              disabled={sinAcuerdos}
              style={sinAcuerdos ? { opacity: 0.5, cursor: "not-allowed" } : {}}
            >
              {sinAcuerdos ? (
                <option value="">Sin acuerdos disponibles</option>
              ) : (
                <>
                  <option value="">Sin acuerdo</option>
                  {opcionesAcuerdo.map((o) => (
                    <option key={o.id} value={String(o.id)}>
                      {o.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        </div>
        {hideFacturacion && cuotas.length > 0 && (
          <div style={{ marginTop: "16px" }}>
            <label
              style={{
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "#6c757d",
                fontWeight: 500,
              }}
            >
              Calendario de facturación
            </label>
            <table
              className="table table-sm"
              style={{ fontSize: "13px", marginTop: "6px", tableLayout: "fixed", width: "100%" }}
            >
              <thead>
                <tr className={styles.tableHead}>
                  <th style={{ width: "10%" }}>#</th>
                  <th style={{ width: "30%" }}>Mes</th>
                  <th style={{ width: "30%", textAlign: "right" }}>Importe</th>
                  <th style={{ width: "30%" }}>N° factura</th>
                </tr>
              </thead>
              <tbody>
                {cuotas.map((cuota, idx) => {
                  const [cMes, cAno] = (cuota.mes || "").split("-");
                  const esSellada = !!cuota.facturanumero;
                  return (
                    <tr key={idx} style={esSellada ? { opacity: 0.5 } : undefined}>
                      <td style={{ color: "#6c757d", width: "10%" }}>{idx + 1}</td>
                      <td style={{ width: "30%" }}>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <select
                            className="form-control form-control-sm"
                            value={cMes || "01"}
                            disabled={esSellada}
                            onChange={(e) => {
                              const updated = [...cuotas];
                              updated[idx] = {
                                ...updated[idx],
                                mes: `${e.target.value}-${cAno || String(CURRENT_YEAR)}`,
                              };
                              setCuotas(updated);
                            }}
                            style={{ width: "100%", ...(esSellada ? { cursor: "not-allowed" } : {}) }}
                          >
                            {MONTHS.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                          <select
                            className="form-control form-control-sm"
                            value={cAno || String(CURRENT_YEAR)}
                            disabled={esSellada}
                            onChange={(e) => {
                              const updated = [...cuotas];
                              updated[idx] = {
                                ...updated[idx],
                                mes: `${cMes || "01"}-${e.target.value}`,
                              };
                              setCuotas(updated);
                            }}
                            style={{ width: "100%", ...(esSellada ? { cursor: "not-allowed" } : {}) }}
                          >
                            {getAniosCarga().map((y) => (
                              <option key={y} value={String(y)}>
                                {y}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td style={{ width: "30%", textAlign: "right" }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control form-control-sm"
                          value={focusedImporteIdx === idx ? cuota.importe ?? "" : formatMiles(cuota.importe)}
                          disabled={esSellada}
                          onFocus={() => setFocusedImporteIdx(idx)}
                          onBlur={() => setFocusedImporteIdx(null)}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "");
                            const updated = [...cuotas];
                            updated[idx] = { ...updated[idx], importe: raw === "" ? 0 : Number(raw) };
                            setCuotas(updated);
                          }}
                          style={{ width: "100%", textAlign: "right", ...(esSellada ? { cursor: "not-allowed" } : {}) }}
                        />
                      </td>
                      <td style={{ width: "30%", color: "#6c757d", fontSize: "12px" }}>{cuota.facturanumero || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className={styles.bodyFooter}>
          <Button color="secondary" outline onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};
